/**
 * bcportal.app landing page: explains that each company's portal has its own address and lets
 * people type theirs and go. No sign-in happens here, and nothing is stored.
 */
import { useId, useState, type FormEvent } from 'react';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DEMO_PORTAL_URL, PORTAL_DOMAIN, parsePortalAddress } from '../utils/rootSite';

interface RootLandingPageProps {
  /** Opens a portal address. Defaults to a full page navigation (tests pass a spy). */
  navigate?: (url: string) => void;
}

export default function RootLandingPage({ navigate = (url) => window.location.assign(url) }: RootLandingPageProps) {
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const r = parsePortalAddress(value);
    if (!r.ok) { setError(r.error); return; }
    setError(null);
    navigate(r.url);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <main id="main-content" className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>
              <h1 className="text-2xl font-semibold tracking-tight">Sign in to your portal</h1>
            </CardTitle>
            <CardDescription>
              Each company has its own portal address, such as <span className="font-medium text-foreground">yourcompany.{PORTAL_DOMAIN}</span>.
              You&apos;ll find it in your invitation email, or ask the company that gave you access.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} noValidate className="space-y-3">
              <Label htmlFor={inputId}>Your portal address</Label>
              <div className="flex items-center gap-2">
                <Input
                  id={inputId}
                  name="portal"
                  value={value}
                  onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
                  placeholder="yourcompany"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="url"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? `${hintId} ${errorId}` : hintId}
                  className="min-w-0 flex-1"
                />
                <span aria-hidden="true" className="shrink-0 text-sm text-muted-foreground">.{PORTAL_DOMAIN}</span>
              </div>
              <p id={hintId} className="text-sm text-muted-foreground">Type the first part of the address, before .{PORTAL_DOMAIN}.</p>
              {error && (
                <p id={errorId} role="alert" className="text-sm text-destructive">{error}</p>
              )}
              <Button type="submit" className="w-full">
                Go to my portal <ArrowRight className="ml-1 size-4" aria-hidden="true" />
              </Button>
            </form>
            <p className="mt-6 text-sm text-muted-foreground">
              Just looking?{' '}
              <a href={DEMO_PORTAL_URL} className="font-medium text-primary underline underline-offset-4 hover:no-underline">
                Try the demo portal
              </a>
              <ExternalLink className="ml-1 inline size-3.5 align-[-2px]" aria-hidden="true" />
            </p>
          </CardContent>
        </Card>
      </main>
      {/* Divs, not <footer>/<nav>: global app CSS styles every footer and nav element (borders, bars). */}
      <div role="contentinfo" className="px-4 pb-6 text-center text-sm text-muted-foreground">
        <div>
          <a href="/legal/privacy-policy" className="underline underline-offset-4 hover:text-foreground">Privacy policy</a>
          <span aria-hidden="true" className="mx-2">·</span>
          <a href="/legal/terms-of-service" className="underline underline-offset-4 hover:text-foreground">Terms of service</a>
        </div>
      </div>
    </div>
  );
}
